import { gql, useMutation } from '@apollo/client';
import { useQueryCoordinate } from '../graphql.hooks';

// Enums matching backend
export enum TEST_GROUP_PARENT_GROUP_ID {
  AUM_ROLLOUT = 'AUM_ROLLOUT'
}

export enum TEST_GROUP_TYPE {
  STRIPE_PRICE_ENTITY = 'STRIPE_PRICE_ENTITY'
}

export const TEST_GROUP_PARENT_TYPES_GET = gql`
  query TestGroupParentTypesGet {
    testGroupParentTypesGet {
      parentTypes
    }
  }
`;

const PRICE_FRAGMENT = gql`
  fragment PriceFields on Price {
    id
    nickname
    unitAmount
    unitAmountDecimal
    annualAUMFeePercent
    displayInterval
    billingScheme
    interval
    intervalCount
    tiersMode
    usageType
    tiers {
      flatAmount
      flatAmountDecimal
      unitAmount
      unitAmountDecimal
      yearlyAmountDecimal
      upTo
    }
  }
`;

export const TEST_GROUPS_GET = gql`
  ${PRICE_FRAGMENT}
  query TestGroupsGet($input: TestGroupsGetInput!) {
    testGroupsGet(input: $input) {
      testGroups {
        testGroupKey
        parentGroupId
        groupId
        type
        typeKey
        createdAt
        deletedAt
        price {
          ...PriceFields
        }
      }
    }
  }
`;

export const TEST_GROUP_GET = gql`
  query TestGroupGet($input: TestGroupGetInput!) {
    testGroupGet(input: $input) {
      testGroup {
        testGroupKey
        parentGroupId
        groupId
        type
        typeKey
        createdAt
        deletedAt
      }
    }
  }
`;

export type TestGroup = {
  testGroupKey: number;
  parentGroupId: TEST_GROUP_PARENT_GROUP_ID;
  groupId: string;
  type: TEST_GROUP_TYPE;
  typeKey: string | null;
  createdAt: string;
  deletedAt: string | null;
  price: TestGroupPrice | null;
};

export type TestGroupsGetInput = {
  parentGroupId?: TEST_GROUP_PARENT_GROUP_ID;
  groupId?: string;
  type?: string;
};

export type TestGroupGetInput = {
  testGroupKey: number;
};

export type TestGroupParentTypesGetData = {
  testGroupParentTypesGet: {
    parentTypes: TEST_GROUP_PARENT_GROUP_ID[];
  };
};

export type TestGroupsGetData = {
  testGroupsGet: {
    testGroups: TestGroup[];
  };
};

export type TestGroupGetData = {
  testGroupGet: {
    testGroup: TestGroup;
  };
};

// Custom hooks
export const useTestGroupParentTypesGet = (options = {}) => {
  return useQueryCoordinate(TEST_GROUP_PARENT_TYPES_GET, options);
};

export const useTestGroupsGet = (input: TestGroupsGetInput, options: any = {}) => {
  return useQueryCoordinate(TEST_GROUPS_GET, {
    ...options,
    variables: { input },
    skip: options.skip !== undefined ? options.skip : false
  });
};

export const useTestGroupGet = (input: TestGroupGetInput, options: any = {}) => {
  return useQueryCoordinate(TEST_GROUP_GET, {
    ...options,
    variables: { input },
    skip: options.skip !== undefined ? options.skip : !input.testGroupKey
  });
};

export const TEST_GROUP_PRICES_GET = gql`
  ${PRICE_FRAGMENT}
  query TestGroupPricesGet {
    testGroupPricesGet {
      prices {
        ...PriceFields
      }
    }
  }
`;

export type PriceTier = {
  flatAmount: number | null;
  flatAmountDecimal: string | null;
  unitAmount: number | null;
  unitAmountDecimal: string | null;
  yearlyAmountDecimal: string | null;
  upTo: number | null;
};

export type TestGroupPrice = {
  id: string;
  nickname: string | null;
  unitAmount: number | null;
  unitAmountDecimal: string | null;
  annualAUMFeePercent: number | null;
  displayInterval: string;
  billingScheme: string | null;
  interval: string | null;
  intervalCount: number | null;
  tiersMode: string | null;
  usageType: string | null;
  tiers: PriceTier[] | null;
};

export type TestGroupPricesGetData = {
  testGroupPricesGet: {
    prices: TestGroupPrice[];
  };
};

export const useTestGroupPricesGet = (options: any = {}) => {
  return useQueryCoordinate(TEST_GROUP_PRICES_GET, options);
};

export const TEST_GROUP_CREATE = gql`
  mutation TestGroupCreate($input: TestGroupCreateInput!) {
    testGroupCreate(input: $input) {
      testGroup {
        testGroupKey
        parentGroupId
        groupId
        type
        typeKey
        createdAt
      }
    }
  }
`;

export type TestGroupCreateInput = {
  parentGroupId: TEST_GROUP_PARENT_GROUP_ID;
  groupId: string;
  type: TEST_GROUP_TYPE;
  typeKey: string;
};

export type TestGroupCreateData = {
  testGroupCreate: {
    testGroup: TestGroup;
  };
};

export const useTestGroupCreate = () => {
  return useMutation<TestGroupCreateData, { input: TestGroupCreateInput }>(TEST_GROUP_CREATE);
};

export const TEST_GROUP_IDS_GET = gql`
  query TestGroupIdsGet($input: TestGroupIdsGetInput!) {
    testGroupIdsGet(input: $input) {
      groupIds
    }
  }
`;

export const TEST_GROUP_VALIDATE = gql`
  query TestGroupValidate($input: TestGroupValidateInput!) {
    testGroupValidate(input: $input) {
      isValid
      failedReasons
    }
  }
`;

export type TestGroupIdsGetInput = {
  parentGroupId: TEST_GROUP_PARENT_GROUP_ID;
};

export type TestGroupValidateInput = {
  parentGroupId: TEST_GROUP_PARENT_GROUP_ID;
  groupId: string;
};

export type TestGroupIdsGetData = {
  testGroupIdsGet: {
    groupIds: string[];
  };
};

export type TestGroupValidationResult = {
  isValid: boolean;
  failedReasons: string[];
};

export type TestGroupValidateData = {
  testGroupValidate: TestGroupValidationResult;
};

export const useTestGroupIdsGet = (input: TestGroupIdsGetInput, options: any = {}) => {
  return useQueryCoordinate(TEST_GROUP_IDS_GET, {
    ...options,
    variables: { input },
    skip: options.skip !== undefined ? options.skip : !input.parentGroupId,
  });
};

export const useTestGroupValidate = (input: TestGroupValidateInput, options: any = {}) => {
  return useQueryCoordinate(TEST_GROUP_VALIDATE, {
    ...options,
    variables: { input },
    skip: options.skip !== undefined ? options.skip : (!input.parentGroupId || !input.groupId),
  });
};

export const TEST_GROUP_DELETE = gql`
  mutation TestGroupDelete($input: TestGroupDeleteInput!) {
    testGroupDelete(input: $input) {
      success
    }
  }
`;

export type TestGroupDeleteInput = {
  testGroupKey: number;
};

export type TestGroupDeleteData = {
  testGroupDelete: {
    success: boolean;
  };
};

export const useTestGroupDelete = () => {
  return useMutation<TestGroupDeleteData, { input: TestGroupDeleteInput }>(TEST_GROUP_DELETE);
};
